import { useState, type FormEvent } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import { KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/Button';
import { Logo } from '@/components/Logo';
import { useAuthStore } from '@/features/auth/auth.store';

/**
 * `destino` chega do roteador, que o leu da URL depois de recusar uma rota
 * protegida. Vem como propriedade, e não de `useSearch`, para que a tela
 * continue montável fora da rota `/entrar` — em teste, por exemplo.
 */
export function LoginPage({ destino = '/' }: { destino?: string } = {}) {
  const { login, verifyMfa, status } = useAuthStore();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const mfaPending = status === 'mfa-pending';

  // Erro vindo do callback do Azure SSO
  const search = useSearch({ strict: false }) as { erro?: string };
  const azureError = search.erro === 'azure';

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mfaPending) {
        await verifyMfa(code);
      } else {
        await login(email, password);
        // Navegação imperativa: depende do resultado da chamada, não de um
        // efeito colateral de renderização.
        if (!useAuthStore.getState().mfaRequired) {
          // `href` e não `to`: o destino é um caminho arbitrário vindo da URL,
          // que o roteador já validou como interno — não uma das rotas
          // conhecidas em tempo de compilação.
          await router.navigate({ href: destino });
        }
        return;
      }
      await router.navigate({ href: destino });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Não foi possível entrar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full md:grid-cols-[5fr_7fr]">
      {/* Painel esquerdo — identidade de marca */}
      <div className="hidden md:flex flex-col justify-between bg-sidebar px-10 py-12">
        <Logo />

        <div>
          <div className="mb-2 h-[3px] w-10 rounded-full gradient-primary" />
          <p className="mt-6 font-heading text-2xl font-bold leading-snug text-white/90">
            Service Desk e<br />Gestão de Projetos
          </p>
          <p className="mt-3 text-sm text-sidebar-foreground/55">
            Plataforma interna · ITIL 4
          </p>
        </div>

        <p className="text-xs text-sidebar-foreground/35">
          Aptum Engenharia
        </p>
      </div>

      {/* Painel direito — formulário */}
      <div className="grid place-items-center bg-background px-6 py-14">
        <div className="w-full max-w-sm animate-slide-up">
          {/* Logo apenas no mobile */}
          <div className="mb-8 flex justify-center md:hidden">
            <Logo />
          </div>

          <h1 className="text-2xl font-bold tracking-tight">
            {mfaPending ? 'Segundo fator' : 'Entrar'}
          </h1>
          <p className="mb-7 mt-1 text-sm text-muted-foreground">
            {mfaPending ? 'Confirme o código do autenticador' : 'Use seu e-mail @aptum.com.br'}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mfaPending ? (
              <Field
                id="code"
                label="Código do autenticador"
                icon={<ShieldCheck className="h-4 w-4" aria-hidden />}
              >
                <input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="000000"
                  className={inputClass + ' text-center font-mono text-lg tracking-[0.4em]'}
                />
              </Field>
            ) : (
              <>
                <Field id="email" label="E-mail">
                  <input
                    id="email"
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="voce@aptum.com.br"
                    className={inputClass}
                  />
                </Field>

                <Field id="password" label="Senha" icon={<KeyRound className="h-4 w-4" aria-hidden />}>
                  <input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className={inputClass}
                  />
                </Field>
              </>
            )}

            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}

            <Button type="submit" loading={busy} size="lg" className="w-full">
              {mfaPending ? 'Confirmar' : 'Entrar'}
            </Button>
          </form>

          {!mfaPending && (
            <>
              <div className="relative my-6 flex items-center">
                <div className="flex-1 border-t border-border/50" />
                <span className="mx-4 text-xs text-muted-foreground">ou</span>
                <div className="flex-1 border-t border-border/50" />
              </div>

              {azureError && (
                <p role="alert" className="mb-3 text-center text-sm text-destructive">
                  Não foi possível entrar com a conta Microsoft. Tente novamente.
                </p>
              )}

              <a
                href="/api/v1/auth/azure/login"
                className={
                  'flex w-full items-center justify-center gap-3 rounded-lg border border-input/80 ' +
                  'bg-muted/40 px-4 py-2.5 text-sm font-medium transition-colors ' +
                  'hover:bg-muted hover:border-input focus-visible:outline-none focus-visible:shadow-glow'
                }
              >
                <MicrosoftIcon />
                Entrar com Microsoft
              </a>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputClass =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow';

function Field({
  id,
  label,
  icon,
  children,
}: {
  id: string;
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium">
        {icon}
        {label}
      </label>
      {children}
    </div>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export { Logo };
